import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import html2pdf from 'html2pdf.js';
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from "recharts";
import { fetchBloodSugarReport } from "./store";
import { Card, Table, Form, Button, FormControl, Modal } from 'react-bootstrap';
import { FaHeartbeat, FaSearch, FaDownload, FaTimes, FaUser, FaVenusMars, FaMobileAlt, FaLayerGroup, FaDotCircle, FaChevronLeft, FaChevronRight, FaChevronUp, FaChevronDown } from "react-icons/fa";
import { MdTrendingUp } from "react-icons/md";
import Swal from 'sweetalert2';
import { ComponentLoader } from '../common/Loader';

const BloodSugarReport = () => {
    const dispatch = useDispatch();
    const [reportData, setReportData] = useState({
        summary: { totalReadings: null, totalGTT: null, totalRBS: null, totalFBS: null, totalHbA1c: null },
        tableData: [],
        barChartData: [],
        healthStatusPieData: [],
        categoryBreakdowns: {}
    });

    // Pagination State
    const [totalCount, setTotalCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [goToPage, setGoToPage] = useState('');
    const [prevFilters, setPrevFilters] = useState(null);
    const [sortBy, setSortBy] = useState('testDate:desc');

    // Export State
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportOption, setExportOption] = useState("currentPage"); // "currentPage", "allPages", "pageRange"
    const [exportRange, setExportRange] = useState("");
    const [isExporting, setIsExporting] = useState(false);

    const initialFilters = {
        gender: 'All',
        ageRange: 'All',
        deviceType: 'All',
        category: 'All',
        sugarLevel: 'All',
        searchTerm: '',
        startDate: '',
        endDate: ''
    };

    const [filters, setFilters] = useState(initialFilters);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimeoutRef = useRef(null);
    const isFetching = useRef(false);
    const lastFetchParams = useRef(null);

    const fetchData = (isPageChange = false) => {
        if (isFetching.current) return;
        setLoading(true);
        isFetching.current = true;

        const currentFilterSet = JSON.stringify({ ...filters, debouncedSearch, sortBy });
        const skipCharts = isPageChange && currentFilterSet === prevFilters;

        const payload = {
            ...filters,
            search: debouncedSearch,
            page: currentPage, 
            limit: itemsPerPage,
            skipCharts,
            sortBy: sortBy.split(':')[0],
            sortOrder: sortBy.split(':')[1]
        };

        dispatch(fetchBloodSugarReport(payload))
            .then((response) => {
                if (response.payload && response.payload.success) {
                    const newData = response.payload.tableData || [];
                    
                    if (skipCharts) {
                         setReportData(prev => ({
                            ...prev,
                            tableData: newData
                        }));
                    } else {
                        setReportData(response.payload);
                        setPrevFilters(currentFilterSet);
                    }

                    setTotalCount(response.payload.totalCount || 0);
                }
                setLoading(false);
                isFetching.current = false;
            })
            .catch((error) => {
                console.error('Error fetching Blood Sugar report:', error);
                setLoading(false);
                isFetching.current = false;
            });
    };

    useEffect(() => {
        const currentParams = JSON.stringify({ ...filters, debouncedSearch, sortBy, currentPage, itemsPerPage });
        if (lastFetchParams.current === currentParams) return;
        lastFetchParams.current = currentParams;

        const currentFilterSet = JSON.stringify({ ...filters, debouncedSearch, sortBy });
        const isPageChange = currentFilterSet === prevFilters;
        fetchData(isPageChange);
    }, [dispatch, debouncedSearch, filters.gender, filters.ageRange, filters.deviceType, filters.category, filters.sugarLevel, filters.startDate, filters.endDate, sortBy, currentPage, itemsPerPage]);

    const handleGoToPage = () => {
        const maxPages = Math.ceil(totalCount / itemsPerPage);
        const pageNum = parseInt(goToPage, 10);
        if (!pageNum || pageNum < 1 || pageNum > maxPages) {
            Swal.fire('Invalid Page', `Total pages: ${maxPages}`, 'warning');
            return;
        }
        setCurrentPage(pageNum);
        setGoToPage('');
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
        if (field === 'category' && value === 'All' && sortBy.startsWith('sugarLevelValue')) {
            setSortBy('testDate:desc');
        }
    };

    const handleSortChange = (newVal) => {
        setSortBy(newVal);
        setCurrentPage(1);
    };

    const renderSortHeader = (label, field, width) => {
        let currentSortField = 'testDate';
        let currentSortOrder = 'desc';
        if (sortBy) {
            const parts = sortBy.split(':');
            currentSortField = parts[0];
            currentSortOrder = parts[1];
        }

        const isSorted = currentSortField === field;
        
        return (
            <th 
                className="sticky-header-th" 
                style={{ width: width, cursor: 'pointer' }}
                onClick={() => {
                    if (isSorted) {
                        handleSortChange(`${field}:${currentSortOrder === 'asc' ? 'desc' : 'asc'}`);
                    } else {
                        handleSortChange(`${field}:desc`);
                    }
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    {label}
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', color: isSorted ? '#4f46e5' : '#cbd5e1' }}>
                        <FaChevronUp size={10} style={{ opacity: isSorted && currentSortOrder === 'asc' ? 1 : 0.3 }} />
                        <FaChevronDown size={10} style={{ opacity: isSorted && currentSortOrder === 'desc' ? 1 : 0.3, marginLeft: '-2px' }} />
                    </div>
                </div>
            </th>
        );
    };

    const handleItemsPerPageChange = (newVal) => {
        setItemsPerPage(Number(newVal));
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setFilters(prev => ({ ...prev, searchTerm: val }));

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(val);
            setCurrentPage(1);
        }, 500);
    };

    const getDeviceType = (val) => {
        const v = (val || '').toLowerCase();
        if (v === 'a' || v === 'android' || v === 'andorid') return 'Android';
        if (v === 'i' || v === 'ios') return 'iOS';
        return '—';
    };

    const clearFilters = () => {
        setFilters(initialFilters);
        setDebouncedSearch('');
        setCurrentPage(1);
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return "—";
        const date = new Date(dateStr);
        return date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getStatusLabel = (level, type) => {
        const val = parseFloat(level);
        if (isNaN(val)) return "Unknown";

        switch (type) {
            case "Fasting Blood Sugar Test":
                if (val < 70) return "Low";
                if (val < 100) return "Normal";
                if (val <= 125) return "Prediabetes";
                return "Diabetes";

            case "HbA1c Test":
                if (val < 5.7) return "Normal";
                if (val <= 6.4) return "Prediabetes";
                return "Diabetes";

            case "Random Blood Sugar Test":
                if (val < 70) return "Low";
                if (val < 140) return "Normal";
                if (val < 200) return "Prediabetes";
                return "Diabetes";

            case "Glucose Tolerance Test":
                if (val < 70) return "Low";
                if (val < 140) return "Normal";
                if (val <= 199) return "Prediabetes";
                return "Diabetes";
            default:
                return "Unknown";
        }
    };

    const generateCSV = (data) => {
        if (!data || data.length === 0) return;

        const headers = ["User ID", "Gender", "Age", "Sugar Level", "Category", "Status", "Reading Date", "Reading Time", "Device"];
        const rows = data.map(item => [
            item.userId || "—",
            item.userGender || "—",
            item.userAge || "—",
            item.sugarLevel || "—",
            item.sugarType || "—",
            getStatusLabel(item.sugarLevel, item.sugarType),
            formatDate(item.testDate),
            formatTime(item.testDate),
            getDeviceType(item.deviceType)
        ]);

        const csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(e => e.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Blood_Sugar_Report_Export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const generatePDF = (data) => {
        if (!data || data.length === 0) return;

        const totalRecords = data.length;
        const generatedDate = new Date().toLocaleDateString('en-GB');
        const generatedTime = new Date().toLocaleTimeString('en-US', { hour12: false });

        // ✅ Changed to 8 rows per page (as per your requirement)
        const rowsPerPage = 8;
        const totalTablePages = Math.ceil(totalRecords / rowsPerPage);
        const totalPDFPages = 1 + totalTablePages;   // 1 summary + table pages

        const styles = `
        <style>
            @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
            
            body { 
                font-family: 'Poppins', sans-serif; 
                color: #1e293b; 
                margin: 0; 
                padding: 0; 
            }
            
            .report-container {
                min-height: 275mm;                    /* Changed from height */
                display: flex;
                flex-direction: column;
                background: #fff;
                padding: 40px 30px 20px;
                box-sizing: border-box;
                page-break-after: always;
            }
            
            .exp-page-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 40px;
            }
            
            .brand-logo { 
                color: #42da9f; 
                font-weight: 800; 
                font-size: 26px; 
            }
            .brand-sub { 
                color: #e05923; 
                font-size: 11px; 
                text-transform: uppercase; 
            }
            
            .report-title { 
                text-align: center; 
                margin: 30px 0 50px; 
            }
            .report-title h1 { 
                font-size: 28px; 
                font-weight: 800; 
                text-transform: uppercase; 
                color: #0f172a; 
            }
            .summary-word::after { 
                content: ''; 
                position: absolute; 
                bottom: -8px; 
                left: 0; 
                width: 100%; 
                height: 3.5px; 
                background: #f70c0c; 
            }
            
            .section-title { 
                color: #f70c0c; 
                font-weight: 800; 
                font-size: 17px; 
                border-left: 4px solid #f70c0c; 
                padding-left: 15px; 
            }
            .section-text { 
                font-size: 13px; 
                color: #475569; 
                line-height: 1.8; 
                text-align: justify; 
            }
            
            .report-table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-top: 20px; 
                border: 1px solid #e2e8f0; 
                border-radius: 12px; 
                overflow: hidden;
            }
            .report-table th { 
                background: #f8fafc; 
                padding: 14px; 
                font-size: 11px; 
                font-weight: 700; 
                text-transform: uppercase; 
            }
            .report-table td { 
                padding: 13px 10px; 
                font-size: 12px; 
                border-bottom: 1px solid #f1f5f9; 
                text-align: center; 
            }
            
            /* ✅ Prevent row from breaking across pages */
            .report-table tr { 
                page-break-inside: avoid; 
                page-break-after: auto; 
            }
            
            .status-badge { 
                font-weight: 800; 
                color: #f70c0c; 
                text-transform: uppercase; 
                font-size: 10px; 
            }
            
            .page-footer {
                margin-top: auto;                    /* ✅ Forces footer to bottom */
                text-align: center; 
                font-size: 10px; 
                color: #94a3b8; 
                border-top: 1.5px solid #f1f5f9; 
                padding-top: 15px;
                text-transform: uppercase; 
                letter-spacing: 1px;
            }
        </style>
    `;

        let htmlContent = '';

        // ==================== PAGE 1: SUMMARY ====================
        htmlContent += `
        <div class="report-container">
            <div class="exp-page-header">
                <div>
                    <div class="brand-logo">MedHorizone</div>
                    <div class="brand-sub">Healthcare Tracking System</div>
                </div>
                <div style="text-align:right">
                    <div style="font-weight:800">Admin</div>
                    <div>${generatedDate} | ${generatedTime}</div>
                </div>
            </div>
            
            <div class="report-title">
                <h1>BLOOD SUGAR <span class="summary-word">SUMMARY</span> REPORT</h1>
            </div>
            
            <div style="margin-bottom:40px">
                <div class="section-title">1. Executive Summary</div>
                <p class="section-text">
                    This report provides a comprehensive overview of the blood sugar metrics recorded within the MedHorizone system for the current period. The data highlights significant trends in glucose management, patient engagement, and overall system utilization. Our primary focus remains on early detection of diabetes risks and maintaining stable blood sugar levels across all age demographics.
                </p>
            </div>
            
            <div style="margin-bottom:40px">
                <div class="section-title">2. Patient Demographics & Engagement</div>
                <p class="section-text">
                    During this reporting cycle, we observed a steady engagement in active users tracking their vitals daily. The majority of our active user base falls within the 30-50 age range, indicating a growing health consciousness among middle-aged patients. Gender distribution remains balanced, with active participation across both male and female users.
                </p>
            </div>
            
            <div style="margin-bottom:40px">
                <div class="section-title">3. Recommendations</div>
                <p class="section-text">
                    Based on the current data, it is recommended to increase patient outreach for those in the 'Prediabetes' and 'Diabetes' categories. Implementing automated health alerts for readings exceeding recommended thresholds could significantly improve intervention times.
                </p>
            </div>
            
            <div class="page-footer">
                Generated by MedHorizone Healthcare System - Confidential Document<br>
                Page 1 of ${totalPDFPages}
            </div>
        </div>
    `;

        // ==================== TABLE PAGES (8 rows each) ====================
        for (let p = 0; p < totalTablePages; p++) {
            const start = p * rowsPerPage;
            const pageData = data.slice(start, start + rowsPerPage);

            htmlContent += `
            <div class="report-container">
                <div class="exp-page-header">
                    <div>
                        <div class="brand-logo" style="font-size:22px;">MedHorizone</div>
                        <div class="brand-sub">Healthcare Tracking System</div>
                    </div>
                    <div style="text-align:right">
                        <div>${generatedDate}</div>
                    </div>
                </div>
                
                <div style="margin-bottom:20px">
                    <div class="section-title" style="font-size:16px;">
                        Blood Sugar Overview - Page ${p + 1}
                    </div>
                </div>
                
                <table class="report-table">
                    <thead>
                        <tr>
                            <th style="width:5%">#</th>
                            <th style="width:15%">User ID</th>
                            <th style="width:15%">Age/Gender</th>
                            <th style="width:10%">Level</th>
                            <th style="width:15%">Test Type</th>
                            <th style="width:12%">Status</th>
                            <th style="width:13%">Date</th>
                            <th style="width:15%">Device</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${pageData.map((item, idx) => `
                            <tr>
                                <td>${start + idx + 1}</td>
                                <td style="word-break: break-all;">${item.userId || '—'}</td>
                                <td>${item.userAge || '—'} / ${item.userGender ? item.userGender.charAt(0).toUpperCase() : '—'}</td>
                                <td style="font-weight:800;">${item.sugarLevel} 
                                    <span style="font-size:10px;color:#64748b;">
                                        ${item.sugarType === 'HbA1c Test' ? '%' : 'mg/dL'}
                                    </span>
                                </td>
                                <td>${item.sugarType}</td>
                                <td>${getStatusLabel(item.sugarLevel, item.sugarType)}</td>
                                <td>${formatDate(item.testDate)}</td>
                                <td>${getDeviceType(item.deviceType)}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                
                <div class="page-footer">
                    Generated by MedHorizone Healthcare System - Confidential Document<br>
                    Page ${p + 2} of ${totalPDFPages}
                </div>
            </div>
        `;
        }

        // ==================== GENERATE PDF ====================
        const element = document.createElement('div');
        element.innerHTML = styles + htmlContent;

        const opt = {
            margin: [10, 15, 10, 15],
            filename: `MedHorizone_Blood_Sugar_Report_${generatedDate.replace(/\//g, '-')}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, letterRendering: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        html2pdf().set(opt).from(element).save().then(() => {
            setIsExporting(false);
            Swal.fire('Success', 'PDF report generated successfully.', 'success');
        });
        setIsExporting(false);
    };


    const performExport = async (format) => {
        setIsExporting(true);
        try {
            let exportData = [];

            if (exportOption === "currentPage") {
                exportData = reportData.tableData;
            } else if (exportOption === "allPages") {
                const response = await dispatch(fetchBloodSugarReport({
                    ...filters,
                    search: debouncedSearch,
                    page: 1,
                    limit: 10000 // Safe limit for bulk export
                }));
                if (response.payload && response.payload.success) {
                    exportData = response.payload.tableData;
                }
            } else if (exportOption === "pageRange") {
                const [start, end] = exportRange.split("-").map(n => parseInt(n.trim()));
                const maxPages = Math.ceil(totalCount / itemsPerPage);

                if (!start || !end || start > end) {
                    Swal.fire("Invalid Range", "Please enter a valid page range (e.g., 1-5).", "error");
                    setIsExporting(false);
                    return;
                }

                if (end > maxPages) {
                    Swal.fire("Invalid Range", `The end page cannot exceed the total number of pages (${maxPages}).`, "warning");
                    setIsExporting(false);
                    return;
                }

                const response = await dispatch(fetchBloodSugarReport({
                    ...filters,
                    search: debouncedSearch,
                    page: 1,
                    limit: end * itemsPerPage
                }));
                if (response.payload && response.payload.success) {
                    const startIndex = (start - 1) * itemsPerPage;
                    exportData = response.payload.tableData.slice(startIndex);
                }
            }

            if (!exportData || exportData.length === 0) {
                Swal.fire("No Data", "No data found for the selected range.", "warning");
            } else {
                if (format === "csv") {
                    generateCSV(exportData);
                } else {
                    generatePDF(exportData);
                }
            }
        } catch (err) {
            console.error("Export failed:", err);
            Swal.fire("Error", "Failed to export data.", "error");
        } finally {
            setIsExporting(false);
            setShowExportModal(false);
        }
    };

    const getPercentage = (cat) => {
        // This calculates percentage based on ALL filtered records (returned by summary/charts)
        // Adjust if backend returns counts per category in summary
        return 0; // Placeholder until integrated summary counts or full list is available
    };

    // Shared Styles
    const filterGroupStyle = { display: 'flex', flexDirection: 'column', gap: '6px', flex: '1' };
    const filterLabelStyle = { fontSize: '11px', fontWeight: '700', color: '#414347', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '2px' };
    const inputWrapperStyle = { position: 'relative', display: 'flex', alignItems: 'center' };
    const iconStyle = { position: 'absolute', left: '10px', fontSize: '14px', color: '#94a3b8', pointerEvents: 'none' };
    const modernInputStyle = { width: '100%', padding: '9px 12px 9px 32px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', color: '#1e293b', background: '#f8fafc', transition: 'all 0.2s', outline: 'none' };

    const paginationBtnStyle = {
        padding: '6px 12px',
        border: '1px solid #e2e8f0',
        background: 'white',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: 600,
        color: '#475569',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    };

    const tableHeaderStyle = {
        position: 'sticky',
        top: '-1px', // Avoid tiny gaps
        zIndex: 10,
        background: '#f9fafb',
        padding: '14px 16px',
        fontSize: '12px',
        fontWeight: 700,
        color: '#475569',
        textAlign: 'center',
        borderBottom: '1px solid #e5e7eb',
        verticalAlign: 'middle'
    };

    const totalPages = Math.ceil(totalCount / itemsPerPage);

    return (
        <div style={{ padding: '24px', background: '#f5f7fa', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700, color: '#1a1a1a', fontSize: '24px' }}>Blood Sugar Analysis</h4>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '32px' }}>
                {[
                    { title: 'Total Tests', value: reportData.summary.totalReadings, icon: <FaLayerGroup />, color: '#eff6ff', iconColor: '#1e40af' },
                    { title: 'Glucose Tolerance Test', value: reportData.summary.totalGTT, icon: <MdTrendingUp />, color: '#ecfdf5', iconColor: '#065f46' },
                    { title: 'Random Blood Sugar Test', value: reportData.summary.totalRBS, icon: <MdTrendingUp />, color: '#fef2f2', iconColor: '#991b1b' },
                    { title: 'Fasting Blood Sugar Test', value: reportData.summary.totalFBS, icon: <FaHeartbeat />, color: '#f3e8ff', iconColor: '#6b21a8' },
                    { title: 'HbA1c Test', value: reportData.summary.totalHbA1c, icon: <FaDotCircle />, color: '#fff7ed', iconColor: '#c2410c' }
                ].map((stat, i) => (
                    <div key={i} style={{ flex: '1', minWidth: '180px', background: stat.color, borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center', minHeight: '110px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>{stat.title}</div>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: stat.iconColor, margin: '8px 0' }}>
                            {stat.value !== null ? stat.value : <ComponentLoader color={stat.iconColor} size="24px" padding="0" />}
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter Section Card */}
            <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '16px', overflow: 'hidden' }}>
                <Card.Body style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: '#374151', fontSize: '14px' }}>Reading Date Range:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Form.Control
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', color: '#374151', background: '#f9fafb', width: '150px' }}
                            />
                            <span style={{ color: '#9ca3af', fontWeight: 500, fontSize: '14px' }}>to</span>
                            <Form.Control
                                type="date"
                                value={filters.endDate}
                                min={filters.startDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', color: '#374151', background: '#f9fafb', width: '150px' }}
                            />
                        </div>
                        <button
                            onClick={clearFilters}
                            style={{
                                padding: '8px 16px',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: 700,
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                textTransform: 'uppercase'
                            }}
                        >
                            <FaTimes style={{ fontSize: '14px' }} /> CLEAR FILTERS
                        </button>
                        <span style={{
                            marginLeft: 'auto',
                            padding: '6px 16px',
                            background: '#f1f5f9',
                            color: '#4f46e5',
                            borderRadius: '20px',
                            fontSize: '14px',
                            fontWeight: 700,
                            letterSpacing: '0.3px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}>
                            Total Count : {totalCount}
                        </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>USER ID</label>
                            <div style={inputWrapperStyle}>
                                <FaSearch style={iconStyle} />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={filters.searchTerm}
                                    onChange={handleSearchChange}
                                    style={modernInputStyle}
                                />
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>AGE</label>
                            <div style={inputWrapperStyle}>
                                <FaUser style={iconStyle} />
                                <Form.Select
                                    value={filters.ageRange}
                                    onChange={(e) => handleFilterChange('ageRange', e.target.value)}
                                    style={modernInputStyle}
                                >
                                    <option>All</option>
                                    <option>1-18</option>
                                    <option>19-25</option>
                                    <option>26-35</option>
                                    <option>36-45</option>
                                    <option>46-60</option>
                                    <option>60+</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>GENDER</label>
                            <div style={inputWrapperStyle}>
                                <FaVenusMars style={iconStyle} />
                                <Form.Select
                                    value={filters.gender}
                                    onChange={(e) => handleFilterChange('gender', e.target.value)}
                                    style={modernInputStyle}
                                >
                                    <option>All</option>
                                    <option>Male</option>
                                    <option>Female</option>
                                    <option>Others</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>DEVICE</label>
                            <div style={inputWrapperStyle}>
                                <FaMobileAlt style={iconStyle} />
                                <Form.Select
                                    value={filters.deviceType}
                                    onChange={(e) => handleFilterChange('deviceType', e.target.value)}
                                    style={modernInputStyle}
                                >
                                    <option>All</option>
                                    <option>Android</option>
                                    <option>iOS</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>CATEGORY</label>
                            <div style={inputWrapperStyle}>
                                <FaLayerGroup style={iconStyle} />
                                <Form.Select
                                    value={filters.category}
                                    onChange={(e) => handleFilterChange('category', e.target.value)}
                                    style={modernInputStyle}
                                >
                                    <option value="All">All</option>
                                    <option>Random Blood Sugar Test</option>
                                    <option>Fasting Blood Sugar Test</option>
                                    <option>HbA1c Test</option>
                                    <option>Glucose Tolerance Test</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>SUGAR STATUS</label>
                            <div style={inputWrapperStyle}>
                                <FaDotCircle style={iconStyle} />
                                <Form.Select
                                    value={filters.sugarLevel}
                                    onChange={(e) => handleFilterChange('sugarLevel', e.target.value)}
                                    style={modernInputStyle}
                                >
                                    <option value="All">All</option>
                                    {filters.category !== "HbA1c Test" && <option value="Low">Low</option>}
                                    <option value="Normal">Normal</option>
                                    <option value="Prediabetes">Prediabetes</option>
                                    <option value="Diabetes">Diabetes</option>
                                </Form.Select>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Charts Section */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '24px', marginBottom: '32px' }}>
                {/* BS Distribution by Gender */}
                <div style={{ gridColumn: 'span 8', background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <h5 style={{ fontSize: '16px', fontWeight: 650, marginBottom: '20px' }}>BS Distribution by Gender</h5>
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={reportData.barChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#6b7280' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                            <Legend verticalAlign="top" align="right" height={36} iconType="circle" />
                            <Bar dataKey="Male" fill="#4f46e5" radius={[4, 4, 0, 0]} barSize={14} />
                            <Bar dataKey="Female" fill="#ec4899" radius={[4, 4, 0, 0]} barSize={14} />
                            <Bar dataKey="Other" fill="#94a3b8" radius={[4, 4, 0, 0]} barSize={14} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Overall Health Status */}
                <div style={{ gridColumn: 'span 4', background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <h6 style={{ fontSize: '13.5px', fontWeight: 700, color: '#1a1a1a', marginBottom: '20px', textTransform: 'uppercase' }}>Overall Health Status</h6>
                    <div style={{ position: 'relative' }}>
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart margin={{ bottom: 20 }}>
                                {reportData.healthStatusPieData.length > 0 ? (
                                    <>
                                        <Pie
                                            data={reportData.healthStatusPieData}
                                            cx="50%"
                                            cy="45%"
                                            innerRadius={65}
                                            outerRadius={95}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {reportData.healthStatusPieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={
                                                    entry.name === 'Normal' ? '#10b981' :
                                                    entry.name === 'Prediabetes' ? '#f59e0b' :
                                                    entry.name === 'Diabetes' ? '#ef4444' : '#6b7280'
                                                } />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend
                                            verticalAlign="bottom"
                                            height={36}
                                            formatter={(value, entry) => {
                                                const total = reportData.totalCount;
                                                const val = entry.payload.value;
                                                const pct = total > 0 ? ((val / total) * 100).toFixed(0) : 0;
                                                return <span style={{ color: '#475569', fontSize: '11px', fontWeight: 600 }}>{value} - {pct}%</span>;
                                            }}
                                        />
                                    </>
                                ) : (
                                    <Pie
                                        data={[{ value: 1 }]}
                                        cx="50%"
                                        cy="45%"
                                        innerRadius={65}
                                        outerRadius={95}
                                        fill="#f8fafc"
                                        dataKey="value"
                                        isAnimationActive={false}
                                    />
                                )}
                            </PieChart>
                        </ResponsiveContainer>
                        <div style={{ position: 'absolute', top: '41%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                            <div style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{totalCount}</div>
                            <div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, marginTop: '2px' }}>Total</div>
                        </div>
                    </div>
                    <div style={{ textAlign: 'center', fontSize: '12px', color: '#f70c0c', marginTop: '10px', fontWeight: '500' }}>
                        Note: ‘Low’ category is not applicable for HbA1c Blood sugar test
                    </div>
                </div>
            </div>

            {/* Reading Category Rows */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '20px', marginBottom: '32px' }}>
                {['Fasting Blood Sugar Test', 'Random Blood Sugar Test', 'HbA1c Test', 'Glucose Tolerance Test'].map((cat, idx) => {
                    const stats = reportData.categoryBreakdowns?.[cat] || { Low: 0, Normal: 0, Prediabetes: 0, Diabetes: 0 };
                    const totalCat = Object.values(stats).reduce((a, b) => a + b, 0);
                    const colors = { Low: '#6b7280', Normal: '#10b981', Prediabetes: '#f59e0b', Diabetes: '#ef4444' };
                    
                    return (
                        <div key={idx} style={{ gridColumn: 'span 3', background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                            <h6 style={{ fontSize: '11px', fontWeight: 800, color: '#1e293b', marginBottom: '20px', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat}</h6>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {['Low', 'Normal', 'Prediabetes', 'Diabetes'].filter(s => cat === 'HbA1c Test' ? s !== 'Low' : true).map((status, i) => {
                                    const count = stats[status] || 0;
                                    const p = totalCat > 0 ? Math.round((count / totalCat) * 100) : 0;
                                    return (
                                        <div key={i}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>{status}</span>
                                                <span style={{ fontSize: '11px', fontWeight: 700, color: colors[status] }}>{p}%</span>
                                            </div>
                                            <div style={{ width: '100%', height: '6px', background: '#f1f5f9', borderRadius: '3px' }}>
                                                <div style={{ width: `${p}%`, height: '100%', background: colors[status], borderRadius: '3px' }}></div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {totalCat === 0 && <div style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', marginTop: '10px' }}>No Data</div>}
                            </div>
                        </div>
                    );
                })}
            </div>

            <style>{`
                .sticky-table {
                    border-collapse: separate !important;
                    border-spacing: 0 !important;
                    overflow: visible !important;
                    position: relative !important;
                }
                .sticky-header-th {
                    position: sticky !important;
                    top: -1px !important;
                    z-index: 11 !important;
                    background: #f9fafb !important;
                    border-bottom: 1px solid #e5e7eb !important;
                }
                /* Ensure parent containers don't clip the sticky header */
                .scroll-container {
                    max-height: 600px;
                    overflow-y: auto !important;
                    overflow-x: auto !important;
                    position: relative !important;
                }
            `}</style>
            {/* Table Section */}
            <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                    <div>
                        <h5 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1a1a1a' }}>Blood Sugar Readings</h5>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <Form.Select 
                             value={itemsPerPage}
                             onChange={(e) => handleItemsPerPageChange(e.target.value)}
                             style={{ 
                                 width: '130px', 
                                 height: '38px', 
                                 borderRadius: '8px', 
                                 fontSize: '13px', 
                                 fontWeight: 600, 
                                 border: '1px solid #e2e8f0',
                                 background: '#f8fafc',
                                 cursor: 'pointer'
                             }}
                        >
                            <option value={10}>10 records</option>
                            <option value={15}>15 records</option>
                            <option value={20}>20 records</option>
                            <option value={25}>25 records</option>
                            <option value={30}>30 records</option>
                            <option value={50}>50 records</option>
                        </Form.Select>
                        {/* <button
                            onClick={() => setShowExportModal(true)}
                            className="btn btn-sm"
                            style={{
                                background: '#10b981',
                                color: 'white',
                                fontWeight: 700,
                                padding: '8px 16px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)',
                                transition: 'all 0.2s',
                                border: 'none',
                                textTransform: 'uppercase',
                                height: '38px'
                            }}
                        >
                            <FaDownload /> EXPORT
                        </button> */}
                    </div>
                </div>
                <div className="scroll-container">
                    {loading && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20 }}>
                            <div style={{ fontWeight: 600, color: '#4f46e5' }}>Loading...</div>
                        </div>
                    )}
                    <Table hover className="sticky-table" style={{ 
                        margin: 0, 
                        tableLayout: 'fixed', 
                        minWidth: '1000px'
                    }}>
                        <thead>
                            <tr>
                                <th className="sticky-header-th" style={{ width: '5%' }}>#</th>
                                {renderSortHeader('User ID', 'userId', '15%')}
                                {renderSortHeader('Gender', 'userGender', '12%')}
                                {renderSortHeader('Age', 'userAge', '8%')}
                                {renderSortHeader('Blood Sugar Level', 'sugarLevelValue', '12%')}
                                {renderSortHeader('Category', 'sugarType', '15%')}
                                {renderSortHeader('Status', 'sugarStatus', '10%')}
                                {renderSortHeader('Date & Time', 'testDate', '15%')}
                                {renderSortHeader('Device', 'userDeviceType', '8%')}
                            </tr>
                        </thead>
                        <tbody>
                             {loading ? (
                                <tr>
                                    <td colSpan="9" style={{ padding: '80px 0' }}>
                                        <ComponentLoader color="#4f46e5" size="40px" />
                                    </td>
                                </tr>
                            ) : (
                                reportData.tableData.map((item, index) => (
                                <tr key={index}>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle', color: '#64748b' }}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                        <div style={{ fontWeight: 600, color: '#4f46e5', fontSize: '14px' }}>{item.userId}</div>
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        {item.userGender ? item.userGender.charAt(0).toUpperCase() + item.userGender.slice(1).toLowerCase() : "—"}
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>{item.userAge ?? '—'}</td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
                                            {item.sugarLevel} <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>{item.sugarType === 'HbA1c Test' ? '%' : 'mg/dL'}</span>
                                        </div>
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#4b5563' }}>{item.sugarType}</div>
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <span style={{
                                            padding: '4px 12px', borderRadius: '20px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                                            background: item.sugarStatus === 'Normal' ? '#d1fae5' : 
                                                        item.sugarStatus === 'Diabetes' ? '#fee2e2' : 
                                                        item.sugarStatus === 'Low' ? '#f1f5f9' : '#fff7ed',
                                            color: item.sugarStatus === 'Normal' ? '#065f46' : 
                                                   item.sugarStatus === 'Diabetes' ? '#991b1b' : 
                                                   item.sugarStatus === 'Low' ? '#64748b' : '#c2410c'
                                        }}>
                                            {item.sugarStatus}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <div style={{ fontSize: '13px', fontWeight: 600 }}>{formatDate(item.testDate)}</div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{formatTime(item.testDate)}</div>
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <span style={{
                                            padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                                            background: getDeviceType(item.userDeviceType) === 'Android' ? '#ecfdf5' : getDeviceType(item.userDeviceType) === 'iOS' ? '#eff6ff' : '#f1f5f9',
                                            color: getDeviceType(item.userDeviceType) === 'Android' ? '#065f46' : getDeviceType(item.userDeviceType) === 'iOS' ? '#1e40af' : '#64748b'
                                        }}>
                                            {getDeviceType(item.userDeviceType)}
                                        </span>
                                    </td>
                                </tr>
                            )))}
                        </tbody>
                    </Table>
                </div>

                <div className="modern-pagination-wrapper">
                    <div className="pagination-left">
                        <div className="d-flex align-items-center gap-2">
                            <span className="pagination-label">Go to:</span>
                            <input type="number" className="pagination-input-box" value={goToPage} onChange={(e) => setGoToPage(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleGoToPage()} />
                            <button className="pagination-go-button" onClick={handleGoToPage}>Go</button>
                        </div>
                        <span className="pagination-label">Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries</span>
                    </div>
                    <div className="pagination-right">
                        <div className="ms-3 d-flex gap-2">
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(1)} 
                                disabled={currentPage === 1}
                            >
                                First
                            </button>
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                                disabled={currentPage === 1}
                            >
                                Prev
                            </button>
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(prev => prev + 1)} 
                                disabled={currentPage * itemsPerPage >= totalCount}
                            >
                                Next
                            </button>
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(Math.ceil(totalCount / itemsPerPage))} 
                                disabled={currentPage * itemsPerPage >= totalCount}
                            >
                                Last
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Export Modal */}
            <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
                <Modal.Header closeButton style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Modal.Title style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                        Export Blood Sugar Report
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '10px' }}>
                            SELECT DATA RANGE
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <Form.Check
                                type="radio"
                                label={`Current Page(${reportData.tableData.length} records)`}
                                name="exportOption"
                                checked={exportOption === "currentPage"}
                                onChange={() => setExportOption("currentPage")}
                                style={{ fontSize: '14px', color: '#1e293b' }}
                            />
                            <Form.Check
                                type="radio"
                                label={`All Pages(${totalCount} records)`}
                                name="exportOption"
                                checked={exportOption === "allPages"}
                                onChange={() => setExportOption("allPages")}
                                style={{ fontSize: '14px', color: '#1e293b' }}
                            />
                            {/* <Form.Check
                                type="radio"
                                label="Specific Page Range"
                                name="exportOption"
                                checked={exportOption === "pageRange"}
                                onChange={() => setExportOption("pageRange")}
                                style={{ fontSize: '14px', color: '#1e293b' }}
                            />
                            {exportOption === "pageRange" && (
                                <Form.Control
                                    type="text"
                                    placeholder="e.g. 1-5"
                                    value={exportRange}
                                    onChange={(e) => setExportRange(e.target.value)}
                                    style={{ marginLeft: '24px', width: '120px', height: '32px', fontSize: '13px' }}
                                />
                            )} */}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
                        <Button
                            variant="primary"
                            disabled={isExporting}
                            onClick={() => performExport("csv")}
                            style={{ flex: 1, background: '#4f46e5', border: 'none', fontWeight: 600, height: '40px' }}
                        >
                            {isExporting ? "Processing..." : "Export as CSV"}
                        </Button>
                        <Button
                            variant="danger"
                            disabled={isExporting}
                            onClick={() => performExport("pdf")}
                            style={{ flex: 1, background: '#ef4444', border: 'none', fontWeight: 600, height: '40px' }}
                        >
                            {isExporting ? "Processing..." : "Export as PDF"}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default BloodSugarReport;
